/// <amd-module name="Release/Landing/LeftPanel/PipelineSearch/PipelineSearchStore" />

import { ReleaseDefinition } from "Release/Client/Contracts/Release";
import { StoreBase } from "DistributedTask/Flux/Store";
import { ActionsHubManager } from "DistributedTask/Flux/ActionsHubManager";
import { PipelineSearchActions, ISearchDefinitionPayload } from "./PipelineSearchActions";
import { PipelinesActions, IRemovePipelinePayload, IRenamePipelinePayload, IRenameFolderPayload, IMovePipelinePayload } from "LeftPanel/PipelinesActions";
import * as Resources from "Release/Landing/Resources";

import { localeFormat } from "VSS/Core/Util/String";
import { announce } from "VSS/Core/Util/Accessibility";
import { NamedStoreEvents } from "Release/Landing/Constants";

export interface IActivePipelinesState {
    searchText: string;
    searchedDefinitions: ReleaseDefinition[] | null;
    searchResultsLoading: boolean;
}

export class PipelineSearchStore extends StoreBase {
    public static getKey(): string {
        return "release-landing-pipelines-search-store";
    }

    public initialize(instanceId?: string): void {
        super.initialize(instanceId);

        this._state = { searchText: "", searchedDefinitions: null, searchResultsLoading: false };
        this._pipelineSearchActions = ActionsHubManager.GetActionsHub<PipelineSearchActions>(PipelineSearchActions);
        this._pipelineActions = ActionsHubManager.GetActionsHub<PipelinesActions>(PipelinesActions);

        this._pipelineSearchActions.searchDefinitions.addListener(this._searchDefinitions);
        this._pipelineActions.removePipeline.addListener(this._removePipeline);
        this._pipelineActions.onRenamePipeline.addListener(this._renamePipeline);
        this._pipelineActions.onMovePipeline.addListener(this._movePipeline);
    }

    public disposeInternal(): void {
        this._pipelineSearchActions.searchDefinitions.removeListener(this._searchDefinitions);
        this._pipelineActions.removePipeline.removeListener(this._removePipeline);
        this._pipelineActions.onRenamePipeline.removeListener(this._renamePipeline);
        this._pipelineActions.onMovePipeline.removeListener(this._movePipeline);
    }

    public getState(): IActivePipelinesState {
        return this._state;
    }

    private _searchDefinitions = (payload: ISearchDefinitionPayload): void => {
        this._state.searchText = payload.searchText;
        this._state.searchedDefinitions = payload.searchedDefinitions;
        this._state.searchResultsLoading = payload.searchResultsLoading;

        // Announce the search results only once the loading is finished
        if (!payload.searchResultsLoading && payload.searchText) {
            let announceMessage = "";
            if (payload.searchedDefinitions && payload.searchedDefinitions.length > 0) {
                announceMessage = localeFormat(Resources.FilteredDefinitionsAnnounceMessage, payload.searchedDefinitions.length);
            }
            else {
                announceMessage = Resources.NoDefinitionsMatchYourFilterText;
            }

            announce(announceMessage);
        }

        this.emitChanged();
    }

    private _removePipeline = (payload: IRemovePipelinePayload): void => {
        if (this._state.searchedDefinitions) {
            const index = this._state.searchedDefinitions.findIndex((def) => def.id === payload.pipelineId);
            if (index >= 0) {
                this._state.searchedDefinitions.splice(index, 1);
                this.emitChanged();
            }
        }
    }

    private _renamePipeline = (payload: IRenamePipelinePayload): void => {
        if (this._state.searchedDefinitions) {
            const match = this._state.searchedDefinitions.find((def) => def.id === payload.pipelineId);
            if (match) {
                match.name = payload.pipelineName;
                this.emitChanged();
                this.emit(NamedStoreEvents.Pileline_Renamed_Moved_Event, {});
            }
        }
    }

    private _movePipeline = (payload: IMovePipelinePayload): void => {
        if (this._state.searchedDefinitions) {
            const match = this._state.searchedDefinitions.find((def) => def.id === payload.pipelineId);
            if (match) {
                match.path = payload.newFolderPath;
                this.emitChanged();
                this.emit(NamedStoreEvents.Pileline_Renamed_Moved_Event, {});
            }
        }
    }

    private _state: IActivePipelinesState;
    private _pipelineSearchActions: PipelineSearchActions;
    private _pipelineActions: PipelinesActions;
}
